import React, {
  ForwardRefRenderFunction,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { MenuItem, SelectChangeEvent } from "@mui/material";
import { Grid } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import RadioButton from "Components/Form/RadioGroup";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import ConfirmationModal from "../../../../../components/Dialogs/ConfirmationModal";
import { Select } from "../../../../../components/Form";
import AutoSuggest from "../../../../../components/Form/AutoSuggest";
import { Color } from "../../../../../components/Form/Button";
import Checkbox from "../../../../../components/Form/Checkbox";
import SkeletonComponent from "../../../../../components/Generic/Skeleton";
import { AvailableTask } from "../../../../../models/available.tasks";
import { GenerateJob } from "../../../../../models/generate.job";
import { GenerateSummaryReport } from "../../../../../models/generate.summary.report";
import { Job } from "../../../../../models/job";
import { ReportChecklist } from "../../../../../models/report.checklist";
import { Task } from "../../../../../models/task";
import JobReportService from "../../../../../services/job.report.service";
import { checkError } from "../../../../../util/checkError";
import {
  ExportReportType,
  LoadReportGroupBy,
  ReportLanguage,
  ReportName,
} from "../../../../../util/enums";
import { handleGeneratePdf } from "../../../../../util/generateReport";
import { ReportOption, ReportOptionText } from "./styles";

export interface GenerateReportModalHandler {
  handleOpenModal: (jobs: number[], isOpen: boolean) => void;
}

interface GenerateReportModalProps {
  report?: ReportChecklist;
  jobs?: Job[];
}

const GenerateReportModal: ForwardRefRenderFunction<
  GenerateReportModalHandler,
  GenerateReportModalProps
> = (props, ref) => {
  const { t: translate } = useTranslation();
  const { report: externalReport, jobs } = props;

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [reportChecklistUUID, setReportChecklistUUID] = useState<string>();
  const [jobsIds, setJobsIds] = useState<number[]>();
  const [tasksGenerate, setTasksGenerate] = useState<Task[]>([]);
  const [language] = useState<ReportLanguage>(ReportLanguage.ENGLISH);
  const [tasks, setTasks] = useState<Task[]>();
  const [sortReport, setSortReport] = useState<string>("");
  const [reportWithPhotos, setReportWithPhotos] = useState<number>(0);

  const firstJobId = useMemo(() => {
    if (jobsIds?.length) return jobsIds[0];

    return undefined;
  }, [jobsIds]);

  const { isFetching, data: reportsChecklist } = useQuery(
    ["report.group.available"],
    () => JobReportService.getAvailableReportsByJobId(firstJobId).then(({ data }) => data),
    {
      enabled: !!(!externalReport && firstJobId),
      refetchOnWindowFocus: false,
      initialData() {
        return [];
      },
    }
  );

  const reportSelected = useMemo(() => {
    const reportChecklist =
      externalReport ??
      reportsChecklist.find(
        (reportChecklist) => reportChecklistUUID && reportChecklist?.uuid === reportChecklistUUID
      );

    return reportChecklist;
  }, [reportsChecklist, reportChecklistUUID]);

  const saveIsDisabled = useMemo(() => {
    if (reportSelected?.name === ReportName.ORDER_SUMMARY_REPORT) return false;

    return (
      !tasksGenerate ||
      (tasksGenerate && tasksGenerate?.length === 0) ||
      (externalReport?.name === ReportName.LOAD_REPORT && !sortReport)
    );
  }, [tasksGenerate, sortReport, externalReport, reportSelected]);

  const generateReport = useMutation(
    ["job.report.generate"],
    (generate: GenerateJob) => JobReportService.generate(generate),
    {
      onSuccess: (response) => {
        if (response?.data && response?.data?.content) {
          handleGeneratePdf({
            data: response.data,
            setOpenModal,
            exportFile: true /* !!(jobsIds && jobsIds?.length > 1) */,
          });
          handleClose();
        }
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const generateReportSummary = useMutation(
    ["job.report.generate.summary"],
    (generate: GenerateSummaryReport) => JobReportService.summary(generate),
    {
      onSuccess: (response) => {
        if (response?.data && response?.data?.content) {
          handleGeneratePdf({ data: response.data, setOpenModal, exportFile: true });
          handleClose();
        }
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const loadAvailableTasks = useMutation(
    ["job.report.available.task"],
    (availableTask: AvailableTask) => JobReportService.availableTasks(availableTask),
    {
      onSuccess: (response) => {
        if (response?.data && Array.isArray(response?.data)) {
          setTasksGenerate([]);
          setTasks(response.data);
        }
      },
      onError: (error: AxiosError<Error>) => {
        checkError(error, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  useEffect(() => {
    if (reportSelected && reportSelected?.name !== ReportName.ORDER_SUMMARY_REPORT) {
      loadAvailableTasks.mutate({
        checklistId: reportSelected?.checklistId,
        jobId: firstJobId,
        reportId: reportSelected?.reportId,
      });
    }
  }, [reportSelected]);

  const tasksId = useMemo(() => {
    if (tasksGenerate?.length) {
      const ids: number[] = [];

      tasksGenerate.forEach((task) => {
        if (task?.id) ids.push(task?.id);
      });

      return ids;
    }

    return [] as number[];
  }, [tasksGenerate]);

  const getDescriptionTask = (task: Task) => {
    if (task?.type && task?.startDate)
      return `${task?.type} - ${format(new Date(task?.startDate), "dd/MM/yyyy")}`;

    if (task?.type) return task?.type;

    return "";
  };

  const handleOpenModal = (jobs: number[], isOpen: boolean) => {
    setJobsIds(jobs);
    setOpenModal(isOpen);
  };

  const handleClose = () => {
    setOpenModal(false);
    setReportChecklistUUID(undefined);
    setJobsIds(undefined);
    setTasksGenerate([]);
    setSortReport("");
    setReportWithPhotos(0);
  };

  useImperativeHandle(
    ref,
    () => ({
      handleOpenModal,
    }),
    []
  );

  const handleSubmit = () => {
    if (!reportSelected) return;
    const generateJobReport = {
      jobs: jobsIds,
      checklistId: reportSelected?.checklistId,
      reportId: reportSelected?.reportId,
      type: ExportReportType.PDF,
      tasks: tasksId,
      language,
      groupBy: sortReport,
      reportWithPhotos: Boolean(reportWithPhotos),
    } as GenerateJob;

    generateReport.mutate(generateJobReport);
  };

  const getOptionsTaskGenerate = (): Task[] => {
    if (jobs?.length) {
      const allJobs =
        jobs && jobs?.length > 0 ? jobs.filter((job) => job?.id && jobsIds?.includes(job?.id)) : [];

      const allTasks: Task[] = [];

      allJobs?.forEach((job) => {
        if (job?.tasks && job?.tasks?.length > 0) {
          const tasksWithJob = job?.tasks.map((task) => {
            return { ...task, job: { reference: job?.reference } as Job } as Task;
          });

          allTasks.push(...(tasksWithJob || []));
        }
      });

      return allTasks;
    }

    return tasks || [];
  };

  const renderReportSelect = () => {
    if (reportSelected && reportSelected?.name) {
      return (
        <Select
          displayEmpty
          label={translate("reportType")}
          value={reportSelected?.name}
          placeholder={translate("select")}
          disabled
          required
        >
          <MenuItem key={reportSelected?.name} value={reportSelected?.name}>
            {reportSelected?.name}
          </MenuItem>
        </Select>
      );
    }

    return (
      <SkeletonComponent isLoading={isFetching}>
        <Select
          name="chooseReport"
          label={translate("reportType")}
          value={reportChecklistUUID}
          onChange={(event: SelectChangeEvent<any>) => setReportChecklistUUID(event.target.value)}
          displayEmpty
          placeholder={translate("select")}
          disabled={generateReport.isLoading}
          required
        >
          {reportsChecklist &&
            reportsChecklist?.map((reportChecklist, index) => (
              <MenuItem
                key={`${index}-report`}
                disabled={reportChecklist?.isDisabled}
                value={reportChecklist?.uuid?.toString()}
              >
                {reportChecklist.name}
              </MenuItem>
            ))}
        </Select>
      </SkeletonComponent>
    );
  };

  const renderTaskAutoSuggest = () => {
    if (reportSelected && reportSelected?.name !== ReportName.ORDER_SUMMARY_REPORT) {
      return (
        <Grid item xs={12}>
          <SkeletonComponent isLoading={loadAvailableTasks.isLoading}>
            <AutoSuggest
              placeholder={translate("select")}
              label={translate("taskReportType")}
              value={tasksGenerate}
              onChange={(tasks: Task[]) => {
                setTasksGenerate(tasks || []);
              }}
              disabled={generateReport.isLoading}
              multiple
              required
              options={getOptionsTaskGenerate()}
              getOptionSelected={(option, value) => option.id === value.id}
              groupBy={
                jobsIds && jobsIds?.length > 1
                  ? (option) => option?.job?.reference?.toString() || ""
                  : undefined
              }
              getOptionLabel={(task: Task) => getDescriptionTask(task)}
              renderOption={(
                props: React.HTMLAttributes<any>,
                task: Task,
                { selected }: { selected: boolean }
              ) => (
                <ReportOption {...props}>
                  <Checkbox checked={selected} />
                  <ReportOptionText>{getDescriptionTask(task)}</ReportOptionText>
                </ReportOption>
              )}
            />
          </SkeletonComponent>
        </Grid>
      );
    }

    return null;
  };

  const renderSortReport = () => {
    if (reportSelected?.name === ReportName.LOAD_REPORT) {
      return (
        <Grid item xs={12}>
          <SkeletonComponent isLoading={loadAvailableTasks.isLoading}>
            <Select
              displayEmpty
              required
              label={translate("sortReportBy")}
              placeholder={translate("select")}
              value={sortReport}
              onChange={(event: SelectChangeEvent<any>) => setSortReport(event.target.value)}
            >
              <MenuItem value={LoadReportGroupBy.LIST}>{translate("sortedByInventory")}</MenuItem>
              <MenuItem value={LoadReportGroupBy.LOADED_INTO}>
                {translate("sortedAndGroupedByStorageBox")}
              </MenuItem>
            </Select>
          </SkeletonComponent>
        </Grid>
      );
    }

    if (reportSelected?.name === ReportName.INVENTORY_CONDITION) {
      return (
        <Grid item xs={12}>
          <SkeletonComponent isLoading={loadAvailableTasks.isLoading}>
            <RadioButton
              onChange={(event) => {
                setReportWithPhotos(Number(event.target.value));
              }}
              options={[
                { label: translate("withoutPhotos"), value: 0 },
                {
                  label: translate("withPhotos"),
                  value: 1,
                },
              ]}
              value={reportWithPhotos}
            />
          </SkeletonComponent>
        </Grid>
      );
    }

    return null;
  };

  return (
    <ConfirmationModal
      open={openModal}
      title={translate("generatePDFReport")}
      closeModal={() => handleClose()}
      submit={() => handleSubmit()}
      textSubmit={translate("confirm")}
      customColorCancel={Color.DARK}
      width="560px !important"
      saveDisabled={saveIsDisabled}
      cancelDisabled={generateReport.isLoading}
      loading={generateReport.isLoading}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} sm={8}>
          {renderReportSelect()}
        </Grid>
        <Grid item xs={12} sm={4}>
          <SkeletonComponent isLoading={isFetching}>
            <Select
              displayEmpty
              label={translate("reportLanguage")}
              value={language}
              disabled
              required
            >
              <MenuItem value={ReportLanguage.ENGLISH}>{ReportLanguage.ENGLISH}</MenuItem>
            </Select>
          </SkeletonComponent>
        </Grid>
        {renderTaskAutoSuggest()}
        {renderSortReport()}
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(GenerateReportModal);
